import React, { useMemo, useState } from "react";
import {
    Alert,
    Button,
    Checkbox,
    Modal,
    Space,
    Table,
    Tag,
    Typography,
    Upload,
    message,
} from "antd";
import {
    CheckOutlined,
    DeleteOutlined,
    EyeOutlined,
    InboxOutlined,
} from "@ant-design/icons";

import { importRateExcel, previewRateExcel } from "../../api/rateApi";
import {
    formatRateAmount,
    formatRateValue,
    getPreviewStatusColor,
    getPreviewStatusLabel,
} from "./rateUi";
import { useTranslation } from "../../i18n/LanguageProvider";

const { Dragger } = Upload;
const { Title, Text } = Typography;

/**
 * Excel import of Ханш: upload -> preview every parsed row with its validation state -> confirm
 * -> import.
 */
function RateExcelUpload({ onImportSuccess }) {
    const { t } = useTranslation();
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewRows, setPreviewRows] = useState([]);
    const [previewSummary, setPreviewSummary] = useState(null);
    const [previewErrors, setPreviewErrors] = useState([]);
    const [previewError, setPreviewError] = useState("");
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [loadingImport, setLoadingImport] = useState(false);
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [confirmedPreview, setConfirmedPreview] = useState(false);
    const [importResult, setImportResult] = useState(null);

    const resetAll = () => {
        setSelectedFile(null);
        setPreviewRows([]);
        setPreviewSummary(null);
        setPreviewErrors([]);
        setPreviewError("");
        setPreviewModalOpen(false);
        setConfirmedPreview(false);
    };

    const importableCount = previewSummary?.validRows ?? 0;

    const previewColumns = useMemo(
        () => [
            {
                title: t("col.row"),
                dataIndex: "rowNumber",
                key: "rowNumber",
                width: 80,
                fixed: "left",
            },
            {
                title: t("col.status"),
                dataIndex: "status",
                key: "status",
                width: 190,
                render: (value) => (
                    <Tag color={getPreviewStatusColor(value)}>{getPreviewStatusLabel(value, t)}</Tag>
                ),
            },
            {
                title: t("col.name"),
                dataIndex: "name",
                key: "name",
                width: 250,
                render: (value) => value || "-",
            },
            {
                title: t("col.currency"),
                dataIndex: "currency",
                key: "currency",
                width: 100,
                render: (value) => (value ? <Tag color="blue">{value}</Tag> : "-"),
            },
            {
                title: t("col.rate"),
                dataIndex: "rate",
                key: "rate",
                width: 120,
                align: "right",
                // Invalid rows keep their raw cell text so the user sees what the file contained.
                render: (value) => {
                    const formatted = formatRateValue(value);
                    return formatted === "-" ? value || "-" : formatted;
                },
            },
            {
                title: t("col.institution"),
                dataIndex: "institution",
                key: "institution",
                width: 150,
                render: (value) => (value ? <Tag color="geekblue">{value}</Tag> : "-"),
            },
            {
                title: t("col.amount"),
                dataIndex: "amount",
                key: "amount",
                width: 160,
                align: "right",
                render: (value) => {
                    const formatted = formatRateAmount(value);
                    return formatted === "-" ? value || "-" : formatted;
                },
            },
            {
                title: t("col.executedDate"),
                dataIndex: "executedDate",
                key: "executedDate",
                width: 160,
                render: (value) => value || "-",
            },
            {
                title: t("col.errors"),
                dataIndex: "errors",
                key: "errors",
                width: 320,
                render: (value) =>
                    value && value.length ? (
                        <Text type="danger" style={{ fontSize: 12 }}>
                            {value.join("; ")}
                        </Text>
                    ) : (
                        "-"
                    ),
            },
        ],
        [t]
    );

    const handlePreviewFile = async (file) => {
        setLoadingPreview(true);
        setPreviewError("");
        setPreviewRows([]);
        setPreviewSummary(null);
        setPreviewErrors([]);
        setConfirmedPreview(false);
        setImportResult(null);

        try {
            const data = await previewRateExcel(file);

            const rows = Array.isArray(data?.rows)
                ? data.rows.map((item, index) => ({
                      ...item,
                      key: `${item?.rowNumber ?? index}-${index}`,
                  }))
                : [];

            setSelectedFile(file);
            setPreviewRows(rows);
            setPreviewSummary(data);
            setPreviewErrors(Array.isArray(data?.errors) ? data.errors : []);
            setPreviewModalOpen(true);
        } catch (error) {
            setSelectedFile(file);
            setPreviewError(error?.message || t("excel.readFailed"));
            setPreviewModalOpen(true);
        } finally {
            setLoadingPreview(false);
        }
    };

    const handleApply = async () => {
        if (!selectedFile) {
            message.error(t("excel.selectFile"));
            return;
        }

        if (importableCount === 0) {
            message.error(t("excel.nothingToImport"));
            return;
        }

        if (!confirmedPreview) {
            message.error(t("excel.confirmFirst"));
            return;
        }

        setLoadingImport(true);
        setPreviewError("");

        try {
            const data = await importRateExcel(selectedFile);

            if (data?.success) {
                message.success(
                    `${t("excel.importable")}: ${data?.savedCount ?? 0}, ${t(
                        "excel.duplicate"
                    )}: ${data?.duplicateWarningCount ?? 0}, ${t("excel.invalid")}: ${
                        data?.failedCount ?? 0
                    }`
                );
                setImportResult(data);
                setPreviewModalOpen(false);
                resetAll();
                onImportSuccess?.(data);
            } else {
                const err = data?.message || t("excel.importFailed");
                setPreviewError(err);
                message.error(err);
            }
        } catch (error) {
            const err = error?.message || t("excel.importFailed");
            setPreviewError(err);
            message.error(err);
        } finally {
            setLoadingImport(false);
        }
    };

    const uploadProps = {
        multiple: false,
        accept: ".xlsx,.xls",
        showUploadList: false,
        beforeUpload: (file) => {
            handlePreviewFile(file);
            return false;
        },
    };

    return (
        <>
            <div className="w-full flex flex-col gap-4">
                <div>
                    <Title level={4} className="!mb-1">
                        {t("excel.title")}
                    </Title>
                    <Text type="secondary">{t("rate.excel.columns")}</Text>
                </div>

                <Dragger {...uploadProps}>
                    <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">{t("excel.drop")}</p>
                    <p className="ant-upload-hint">{t("excel.hint")}</p>
                </Dragger>

                <Space wrap>
                    {selectedFile ? (
                        <Tag color="blue">{selectedFile.name}</Tag>
                    ) : (
                        <Tag>{t("excel.noFile")}</Tag>
                    )}

                    {loadingPreview && <Tag color="processing">{t("excel.reading")}</Tag>}

                    {previewSummary && (
                        <>
                            <Tag color="green">{`${t("excel.importable")}: ${previewSummary.validRows ?? 0}`}</Tag>
                            <Tag color="orange">
                                {`${t("excel.duplicate")}: ${previewSummary.duplicateRows ?? 0}`}
                            </Tag>
                            <Tag color="red">{`${t("excel.invalid")}: ${previewSummary.invalidRows ?? 0}`}</Tag>
                        </>
                    )}

                    {selectedFile && (
                        <Button icon={<EyeOutlined />} onClick={() => setPreviewModalOpen(true)}>
                            {t("action.preview")}
                        </Button>
                    )}

                    {selectedFile && (
                        <Button danger icon={<DeleteOutlined />} onClick={resetAll}>
                            {t("action.clear")}
                        </Button>
                    )}
                </Space>

                {previewError && !previewModalOpen && (
                    <Alert
                        type="error"
                        showIcon
                        message={t("excel.error")}
                        description={previewError}
                    />
                )}

                {importResult && (
                    <Alert
                        type="success"
                        showIcon
                        message={t("excel.importOk")}
                        description={importResult.message}
                    />
                )}
            </div>

            <Modal
                title={t("excel.previewTitle")}
                open={previewModalOpen}
                onCancel={() => setPreviewModalOpen(false)}
                width={1280}
                footer={
                    <Space>
                        <Button onClick={() => setPreviewModalOpen(false)}>{t("action.close")}</Button>
                        <Button danger onClick={resetAll}>
                            {t("action.removeFile")}
                        </Button>
                        <Button
                            type="primary"
                            icon={<CheckOutlined />}
                            loading={loadingImport}
                            onClick={handleApply}
                            disabled={loadingPreview || importableCount === 0 || !confirmedPreview}
                        >
                            {`${t("excel.import")} (${importableCount})`}
                        </Button>
                    </Space>
                }
            >
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                    {selectedFile && !previewError && (
                        <Alert
                            showIcon
                            type={importableCount > 0 ? "success" : "warning"}
                            message={importableCount > 0 ? t("excel.ready") : t("excel.notReady")}
                            description={
                                <div>
                                    <div>
                                        <strong>{t("excel.file")}:</strong> {selectedFile.name}
                                    </div>
                                    <div>
                                        <strong>{t("excel.totalRows")}:</strong>{" "}
                                        {previewSummary?.totalRows ?? 0}
                                    </div>
                                    <div>
                                        <strong>{t("excel.importable")}:</strong>{" "}
                                        {previewSummary?.validRows ?? 0}
                                    </div>
                                    <div>
                                        <strong>{t("excel.duplicate")}:</strong>{" "}
                                        {previewSummary?.duplicateRows ?? 0}{" "}
                                        <Text type="secondary">({t("excel.duplicateHint")})</Text>
                                    </div>
                                    <div>
                                        <strong>{t("excel.invalid")}:</strong>{" "}
                                        {previewSummary?.invalidRows ?? 0}
                                    </div>
                                </div>
                            }
                        />
                    )}

                    {previewError && (
                        <Alert
                            type="error"
                            showIcon
                            message={t("excel.error")}
                            description={previewError}
                        />
                    )}

                    {previewErrors.length > 0 && (
                        <Alert
                            type="warning"
                            showIcon
                            message={t("excel.fileError")}
                            description={
                                <ul style={{ margin: 0, paddingLeft: 18 }}>
                                    {previewErrors.map((item, index) => (
                                        <li key={index}>{item}</li>
                                    ))}
                                </ul>
                            }
                        />
                    )}

                    <Checkbox
                        checked={confirmedPreview}
                        onChange={(e) => setConfirmedPreview(e.target.checked)}
                        disabled={importableCount === 0}
                    >
                        {t("excel.confirm")}
                    </Checkbox>

                    <Table
                        columns={previewColumns}
                        dataSource={previewRows}
                        bordered
                        size="small"
                        pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: [10, 20, 50] }}
                        scroll={{ x: 1700, y: 460 }}
                        rowClassName={(record) => (record.valid ? "" : "row-unfilled")}
                        locale={{
                            emptyText: loadingPreview ? t("excel.reading") : t("excel.noRows"),
                        }}
                    />
                </Space>
            </Modal>
        </>
    );
}

export default RateExcelUpload;
